export interface BulkIncomeStatementItem {
  symbol: string;
  incomeStatement: IncomeStatementItem[];
}

export interface BulkIncomeStatementRequest {
  symbols: string[];
}

export interface BulkIncomeStatementResponse {
  data: BulkIncomeStatementItem[];
  error: string;
  message: string;
  success: boolean;
}

export interface BulkBalanceSheetItem {
  symbol: string;
  balanceSheet: BalanceSheetItem[];
}

export interface BulkBalanceSheetRequest {
  symbols: string[];
}

export interface BulkBalanceSheetResponse {
  data: BulkBalanceSheetItem[];
  error: string;
  message: string;
  success: boolean;
}

export interface BulkCashFlowStatementItem {
  symbol: string;
  cashFlowStatement: CashFlowStatementItem[];
}

export interface BulkCashFlowStatementRequest {
  symbols: string[];
}

export interface BulkCashFlowStatementResponse {
  data: BulkCashFlowStatementItem[];
  error: string;
  message: string;
  success: boolean;
}
// Financial Integration types

// Income Statement data item
export interface IncomeStatementItem {
  growthRevenue: number;
  acceptedDate: string;
  bottomLineNetIncome: number;
  cik: string;
  costAndExpenses: number;
  costOfRevenue: number;
  date: string;
  depreciationAndAmortization: number;
  ebit: number;
  ebitda: number;
  eps: number;
  epsDiluted: number;
  filingDate: string;
  fiscalYear: string;
  generalAndAdministrativeExpenses: number;
  grossProfit: number;
  incomeBeforeTax: number;
  incomeTaxExpense: number;
  interestExpense: number;
  interestIncome: number;
  netIncome: number;
  netIncomeDeductions: number;
  netIncomeFromContinuingOperations: number;
  netIncomeFromDiscontinuedOperations: number;
  netInterestIncome: number;
  nonOperatingIncomeExcludingInterest: number;
  operatingExpenses: number;
  operatingIncome: number;
  otherAdjustmentsToNetIncome: number;
  otherExpenses: number;
  period: string;
  reportedCurrency: string;
  researchAndDevelopmentExpenses: number;
  revenue: number;
  sellingAndMarketingExpenses: number;
  sellingGeneralAndAdministrativeExpenses: number;
  symbol: string;
  totalOtherIncomeExpensesNet: number;
  weightedAverageShsOut: number;
  weightedAverageShsOutDil: number;
}

// Income Statement query parameters
export interface GetIncomeStatementQuery {
  symbol: string;
  period: string;
}

// Income Statement response
export interface GetIncomeStatementResponse {
  data: IncomeStatementItem[];
  error: string;
  message: string;
  success: boolean;
}

// Balance Sheet data item
export interface BalanceSheetItem {
  date: string;
  symbol: string;
  reportedCurrency: string;
  cik: string;
  filingDate: string;
  acceptedDate: string;
  fiscalYear: string;
  period: string;
  cashAndCashEquivalents: number;
  shortTermInvestments: number;
  cashAndShortTermInvestments: number;
  netReceivables: number;
  accountsReceivables: number;
  otherReceivables: number;
  inventory: number;
  prepaids: number;
  otherCurrentAssets: number;
  totalCurrentAssets: number;
  propertyPlantEquipmentNet: number;
  goodwill: number;
  intangibleAssets: number;
  goodwillAndIntangibleAssets: number;
  longTermInvestments: number;
  taxAssets: number;
  otherNonCurrentAssets: number;
  totalNonCurrentAssets: number;
  otherAssets: number;
  totalAssets: number;
  totalPayables: number;
  accountPayables: number;
  otherPayables: number;
  accruedExpenses: number;
  shortTermDebt: number;
  capitalLeaseObligationsCurrent: number;
  taxPayables: number;
  deferredRevenue: number;
  otherCurrentLiabilities: number;
  totalCurrentLiabilities: number;
  longTermDebt: number;
  capitalLeaseObligationsNonCurrent: number;
  deferredRevenueNonCurrent: number;
  deferredTaxLiabilitiesNonCurrent: number;
  otherNonCurrentLiabilities: number;
  totalNonCurrentLiabilities: number;
  otherLiabilities: number;
  capitalLeaseObligations: number;
  totalLiabilities: number;
  treasuryStock: number;
  preferredStock: number;
  commonStock: number;
  retainedEarnings: number;
  additionalPaidInCapital: number;
  accumulatedOtherComprehensiveIncomeLoss: number;
  otherTotalStockholdersEquity: number;
  totalStockholdersEquity: number;
  totalEquity: number;
  minorityInterest: number;
  totalLiabilitiesAndTotalEquity: number;
  totalInvestments: number;
  totalDebt: number;
  netDebt: number;
}

// Balance Sheet query parameters
export interface GetBalanceSheetQuery {
  symbol: string;
  period: string;
}

// Balance Sheet response
export interface GetBalanceSheetResponse {
  data: BalanceSheetItem;
  error: string;
  message: string;
  success: boolean;
}

// Cash Flow Statement data item
export interface CashFlowStatementItem {
  date: string;
  symbol: string;
  reportedCurrency: string;
  cik: string;
  filingDate: string;
  acceptedDate: string;
  fiscalYear: string;
  period: string;
  netIncome: number;
  depreciationAndAmortization: number;
  deferredIncomeTax: number;
  stockBasedCompensation: number;
  changeInWorkingCapital: number;

  // Changed fields according to instructions
  changeInAccountsReceivable: number; // Change in Accounts Receivable
  changeInInventory: number; // Change in Inventory
  changeInAccountsPayable: number; // Change in Accounts Payable
  changeInUnearnedRevenue: number; // Change in Unearned Revenue
  changeInOtherNetOperatingAssets: number; // Change in Other Net Operating Assets

  otherWorkingCapital: number;
  otherNonCashItems: number;
  netCashProvidedByOperatingActivities: number;
  investmentsInPropertyPlantAndEquipment: number;
  acquisitionsNet: number;
  purchasesOfInvestments: number;
  salesMaturitiesOfInvestments: number;
  otherInvestingActivities: number;
  netCashProvidedByInvestingActivities: number;
  netDebtIssuance: number;
  longTermNetDebtIssuance: number;
  shortTermNetDebtIssuance: number;
  netStockIssuance: number;
  netCommonStockIssuance: number;
  commonStockIssuance: number;
  commonStockRepurchased: number;
  netPreferredStockIssuance: number;
  netDividendsPaid: number;
  commonDividendsPaid: number;
  preferredDividendsPaid: number;
  otherFinancingActivities: number;
  netCashProvidedByFinancingActivities: number;
  effectOfForexChangesOnCash: number;
  netChangeInCash: number;
  cashAtEndOfPeriod: number;
  cashAtBeginningOfPeriod: number;
  operatingCashFlow: number;
  capitalExpenditure: number;
  freeCashFlow: number;
  incomeTaxesPaid: number;
  interestPaid: number;
}

// Cash Flow Statement query parameters
export interface GetCashFlowStatementQuery {
  symbol: string;
  period: string;
}

// Cash Flow Statement response
export interface GetCashFlowStatementResponse {
  data: CashFlowStatementItem;
  error: string;
  message: string;
  success: boolean;
}

// Ratios data item
export interface RatiosItem {
  marketCapGrowth: number;
  symbol: string;
  date: string;
  fiscalYear: string;
  period: string;
  reportedCurrency: string;
  marketCap: number; // Market Capitalization
  previousClose: number; // Last Close Price
  grossProfitMargin: number;
  ebitMargin: number;
  ebitdaMargin: number;
  operatingProfitMargin: number;
  pretaxProfitMargin: number;
  continuousOperationsProfitMargin: number;
  netProfitMargin: number;
  bottomLineProfitMargin: number;
  receivablesTurnover: number;
  payablesTurnover: number;
  inventoryTurnover: number;
  fixedAssetTurnover: number;
  assetTurnover: number;
  currentRatio: number;
  quickRatio: number;
  solvencyRatio: number;
  cashRatio: number;
  priceToEarningsRatio: number;
  priceToEarningsGrowthRatio: number;
  forwardPriceToEarningsGrowthRatio: number;
  priceToBookRatio: number;
  priceToBookValueRatio: number; // P/TBV Ratio
  priceToSalesRatio: number;
  priceToFreeCashFlowRatio: number;
  priceToOperatingCashFlowRatio: number;
  debtToAssetsRatio: number;
  debtToEquityRatio: number;
  debtToCapitalRatio: number;
  longTermDebtToCapitalRatio: number;
  financialLeverageRatio: number;
  workingCapitalTurnoverRatio: number;
  operatingCashFlowRatio: number;
  operatingCashFlowSalesRatio: number;
  freeCashFlowOperatingCashFlowRatio: number;
  debtServiceCoverageRatio: number;
  interestCoverageRatio: number;
  shortTermOperatingCashFlowCoverageRatio: number;
  operatingCashFlowCoverageRatio: number;
  capitalExpenditureCoverageRatio: number;
  dividendPaidAndCapexCoverageRatio: number;
  dividendPayoutRatio: number;
  dividendYield: number;
  dividendYieldPercentage: number;
  revenuePerShare: number;
  netIncomePerShare: number;
  interestDebtPerShare: number;
  cashPerShare: number;
  bookValuePerShare: number;
  tangibleBookValuePerShare: number;
  shareholdersEquityPerShare: number;
  operatingCashFlowPerShare: number;
  capexPerShare: number;
  freeCashFlowPerShare: number;
  netIncomePerEBT: number;
  ebtPerEbit: number;
  priceToFairValue: number;
  debtToMarketCap: number;
  effectiveTaxRate: number;
  enterpriseValueMultiple: number;
  dividendPerShare: number;
}

// Ratios query parameters
export interface GetRatiosQuery {
  symbol: string;
  period: string;
}

// Ratios response
export interface GetRatiosResponse {
  data: RatiosItem;
  error: string;
  message: string;
  success: boolean;
}

// Financial Ratios data item (for financial-ratios endpoint)
export interface FinancialRatiosItem {
  symbol: string;
  date: string;
  fiscalYear: string;
  period: string;
  reportedCurrency: string;
  grossProfitMargin: number;
  ebitMargin: number;
  ebitdaMargin: number;
  operatingProfitMargin: number;
  pretaxProfitMargin: number;
  continuousOperationsProfitMargin: number;
  netProfitMargin: number;
  bottomLineProfitMargin: number;
  receivablesTurnover: number;
  payablesTurnover: number;
  inventoryTurnover: number;
  fixedAssetTurnover: number;
  assetTurnover: number;
  currentRatio: number;
  quickRatio: number;
  solvencyRatio: number;
  cashRatio: number;
  priceToEarningsRatio: number;
  priceToEarningsGrowthRatio: number;
  forwardPriceToEarningsGrowthRatio: number;
  priceToBookRatio: number;
  priceToSalesRatio: number;
  priceToFreeCashFlowRatio: number;
  priceToOperatingCashFlowRatio: number;
  debtToAssetsRatio: number;
  debtToEquityRatio: number;
  debtToCapitalRatio: number;
  longTermDebtToCapitalRatio: number;
  financialLeverageRatio: number;
  workingCapitalTurnoverRatio: number;
  operatingCashFlowRatio: number;
  operatingCashFlowSalesRatio: number;
  freeCashFlowOperatingCashFlowRatio: number;
  debtServiceCoverageRatio: number;
  interestCoverageRatio: number;
  shortTermOperatingCashFlowCoverageRatio: number;
  operatingCashFlowCoverageRatio: number;
  capitalExpenditureCoverageRatio: number;
  dividendPaidAndCapexCoverageRatio: number;
  dividendPayoutRatio: number;
  dividendYield: number;
  dividendYieldPercentage: number;
  revenuePerShare: number;
  netIncomePerShare: number;
  interestDebtPerShare: number;
  cashPerShare: number;
  bookValuePerShare: number;
  tangibleBookValuePerShare: number;
  shareholdersEquityPerShare: number;
  operatingCashFlowPerShare: number;
  capexPerShare: number;
  freeCashFlowPerShare: number;
  netIncomePerEBT: number;
  ebtPerEbit: number;
  priceToFairValue: number;
  debtToMarketCap: number;
  effectiveTaxRate: number;
  enterpriseValueMultiple: number;
  dividendPerShare: number;
  // Enterprise Value ratios (Trailing Twelve Months)
  // These fields may be numbers or strings (e.g., "-" if not available)
  enterpriseValueToRevenue?: number | string;
  enterpriseValueToEBITDA?: number | string;
  enterpriseValueToOperatingCashFlow?: number | string;
  enterpriseValueToFreeCashFlow?: number | string;
  enterpriseValueToInvestedCapital?: number | string;
}

// Financial Ratios query parameters
export interface GetFinancialRatiosQuery {
  symbol: string;
  period: string;
  tabAccessToken?: string;
}

// Financial Ratios response
export interface GetFinancialRatiosResponse {
  data: FinancialRatiosItem[];
  error: string;
  message: string;
  success: boolean;
}

// Earnings data item
export interface EarningsItem {
  date: string;
  epsActual: number;
  epsEstimated: number;
  lastUpdated: string;
  revenueActual: number;
  revenueEstimated: number;
  symbol: string;
}

// Earnings query parameters
export interface GetEarningsQuery {
  symbol: string;
}

// Earnings response
export interface GetEarningsResponse {
  data: EarningsItem[];
  error: string;
  message: string;
  success: boolean;
}

// Earnings call transcript item
export interface EarningsCallTranscriptItem {
  content: string;
  date: string;
  period: string;
  symbol: string;
  year: number;
}

// Earnings call transcript query parameters
export interface GetEarningsCallTranscriptQuery {
  symbol: string;
  year?: number;
  quarter?: number;
  limit?: number;
}

// Earnings call transcript response
export interface GetEarningsCallTranscriptResponse {
  data: EarningsCallTranscriptItem[];
  error: string;
  message: string;
  success: boolean;
}

// Analyst rating item
export interface AnalystRatingItem {
  symbol: string;
  date: string;
  analystRatingsStrongBuy: number;
  analystRatingsBuy: number;
  analystRatingsHold: number;
  analystRatingsSell: number;
  analystRatingsStrongSell: number;
}

// Analyst rating query parameters
export interface GetAnalystRatingQuery {
  symbol: string;
  tabAccessToken?: string;
}

// Analyst rating response
export interface GetAnalystRatingResponse {
  data: AnalystRatingItem[];
  error: string;
  message: string;
  success: boolean;
}

// Analyst rating history item
export interface AnalystRatingHistoryItem {
  action: string;
  gradingCompany: string;
  newGrade: string;
  newsBaseURL: string;
  newsPublisher: string;
  newsTitle: string;
  newsURL: string;
  previousGrade: string;
  priceWhenPosted: number;
  publishedDate: string;
  symbol: string;
}

// Analyst rating history query parameters
export interface GetAnalystRatingHistoryQuery {
  symbol: string;
  page: number;
  limit: number;
  tabAccessToken?: string;
}

// Analyst rating history response
export interface GetAnalystRatingHistoryResponse {
  data: AnalystRatingHistoryItem[];
  error: string;
  message: string;
  success: boolean;
}

// Dividend summary item
export interface DividendSummaryItem {
  annualPayout: number;
  divYield: number;
  fiveYearGrowthRate: number;
  payoutRatio: number;
}

// Dividend summary query parameters
export interface GetDividendSummaryQuery {
  symbol: string;
}

// Dividend summary response
export interface GetDividendSummaryResponse {
  data: DividendSummaryItem;
  error: string;
  message: string;
  success: boolean;
}

// Dividend history item
export interface DividendHistoryItem {
  adjDividend: number;
  annualPayoutFWD: number;
  date: string;
  declarationDate: string;
  dividend: number;
  frequency: string;
  paymentDate: string;
  recordDate: string;
  symbol: string;
  yield: number;
}

// Dividend history query parameters
export interface GetDividendHistoryQuery {
  symbol: string;
}

// Dividend history response
export interface GetDividendHistoryResponse {
  data: DividendHistoryItem[];
  error: string;
  message: string;
  success: boolean;
}

// Beneficial ownership item
export interface BeneficialOwnershipItem {
  cik: string;
  symbol: string;
  filingDate: string;
  acceptedDate: string;
  cusip: string;
  nameOfReportingPerson: string;
  citizenshipOrPlaceOfOrganization: string;
  soleVotingPower: string;
  sharedVotingPower: string;
  soleDispositivePower: string;
  sharedDispositivePower: string;
  amountBeneficiallyOwned: string;
  percentOfClass: string;
  currentValue?: string;
  percentOfTotalAssetsManaged?: string;
  typeOfReportingPerson: string;
  url: string;
}

// Beneficial ownership query parameters
export interface GetBeneficialOwnershipQuery {
  symbol: string;
}

// Beneficial ownership response
export interface GetBeneficialOwnershipResponse {
  data: BeneficialOwnershipItem[];
  error: string;
  message: string;
  success: boolean;
}

// Insider trading item
export interface InsiderTradingItem {
  acquisitionOrDisposition: string;
  companyCik: string;
  directOrIndirect: string;
  filingDate: string;
  formType: string;
  price: number;
  reportingCik: string;
  reportingName: string;
  securitiesOwned: number;
  securitiesTransacted: number;
  securityName: string;
  symbol: string;
  transactionDate: string;
  transactionType: string;
  typeOfOwner: string;
  url: string;
}

// Insider trading query parameters
export interface GetInsiderTradingQuery {
  symbol: string;
  page?: number;
  limit?: number;
}

// Insider trading response
export interface GetInsiderTradingResponse {
  data: InsiderTradingItem[];
  error: string;
  message: string;
  success: boolean;
}

// Historical price EOD item
export interface HistoricalPriceEodItem {
  symbol: string;
  date: string;
  adjOpen: number;
  adjHigh: number;
  adjLow: number;
  adjClose: number;
  volume: number;
  dividend?: number | null;
  yield?: number | null;
  numberOfAnalysts?: number | null;
}

// Historical price EOD query parameters
export interface GetHistoricalPriceEodQuery {
  symbol: string;
  from?: string;
  to?: string;
}

// Historical price EOD response
export interface GetHistoricalPriceEodResponse {
  data: HistoricalPriceEodItem[];
  error: string;
  message: string;
  success: boolean;
}

export interface BulkProfileRequest {
  symbols: string[];
}

export interface BulkProfileItem {
  companyName: string;
  employees: string;
  enterpriseValue: string;
  industry: string;
  marketCap: string;
  sector: string;
  symbol: string;
}

export interface BulkProfileResponse {
  data: BulkProfileItem[];
  error: string;
  message: string;
  success: boolean;
}

export interface RankingScoreRequest {
  symbols: string[];
}

export interface RankingScoreDetail {
  symbol: string;
  date: string;
  total_score: number;
  rating: string;
}

export interface RankingScoreOverall {
  symbol: string;
  date: string;
  liquidity_score: RankingScoreDetail;
  profitability_score: RankingScoreDetail;
  financial_strength_score: RankingScoreDetail;
  growth_score: RankingScoreDetail;
  momentum_score: RankingScoreDetail;
  dividend_score: RankingScoreDetail;
  gf_score: RankingScoreDetail;
  average_score: RankingScoreDetail;
}

export interface RankingScoreItem {
  symbol: string;
  overallRank: RankingScoreOverall;
  averageRanking: number;
}

export interface RankingScoreResponse {
  data: RankingScoreItem[];
  error: string;
  message: string;
  success: boolean;
}

export interface RatingRequest {
  symbols: string[];
}

export interface RatingData {
  symbol: string;
  strongBuy: number;
  buy: number;
  hold: number;
  sell: number;
  strongSell: number;
  consensus: string;
}

export interface RatingItem {
  symbol: string;
  ratingData: RatingData;
  saAnalystsCovering: number;
  wallStreetAnalysts: number;
}

export interface RatingResponse {
  data: RatingItem[];
  error: string;
  message: string;
  success: boolean;
}

export interface DividendsBulkRequest {
  symbols: string[];
}

export interface DividendsBulkItem {
  symbol: string;
  dividends: DividendHistoryItem[];
}

export interface DividendsBulkResponse {
  data: DividendsBulkItem[];
  error: string;
  message: string;
  success: boolean;
}

export interface GetFinancialAnalyticsQuery {
  symbol: string;
}

export interface FinancialAnalyticsCompanyProfile {
  address: string;
  averageVolume: string;
  beta: string;
  ceo: string;
  change: string;
  changePercentage: string;
  cik: string;
  city: string;
  companyName: string;
  country: string;
  currency: string;
  cusip: string;
  defaultImage: string;
  description: string;
  exchange: string;
  exchangeFullName: string;
  fullTimeEmployees: string;
  image: string;
  industry: string;
  ipoDate: string;
  isActivelyTrading: string;
  isAdr: string;
  isEtf: string;
  isFund: string;
  isin: string;
  lastDividend: string;
  marketCap: string;
  pe: string;
  phone: string;
  price: string;
  range: string;
  sector: string;
  state: string;
  symbol: string;
  volume: string;
  website: string;
  zip: string;
  logoURL: string;
}

export interface FinancialAnalyticsGradeConsensus {
  buy: number;
  consensus: string;
  hold: number;
  sell: number;
  strongBuy: number;
  strongSell: number;
  symbol: string;
}

export interface FinancialAnalyticsPriceTargetConsensus {
  symbol: string;
  targetConsensus: number;
  targetHigh: number;
  targetLow: number;
  targetMedian: number;
}

export interface FinancialAnalyticsData {
  changePercentage: number;
  companyProfile: FinancialAnalyticsCompanyProfile;
  gradeConsensus: FinancialAnalyticsGradeConsensus;
  halal_status: string;
  logoURL: string;
  overallRank: RankingScoreOverall;
  priceChange: number;
  priceTargetConsensus: FinancialAnalyticsPriceTargetConsensus;
  symbol: string;
}

export interface GetFinancialAnalyticsResponse {
  data: FinancialAnalyticsData;
  error: string;
  message: string;
  success: boolean;
}

export interface GetFinancialAnalyticsListQuery {
  limit?: number;
  offset?: number;
}

export interface GetFinancialAnalyticsListResponse {
  data: FinancialAnalyticsData[];
  error: string;
  message: string;
  success: boolean;
}

// Beneficial ownership item

export interface GetMarginOfSafetyQuery {
  symbol: string;
  tabAccessToken?: string;
}

export interface MarginOfSafetyData {
  currentPrice: number;
  date: string;
  intrinsicValue: number;
  isOvervalued: boolean;
  isUndervalued: boolean;
  marginOfSafety: number;
  priceDifference: number;
  symbol: string;
  targetPrice: number;
}

export interface GetMarginOfSafetyResponse {
  data: MarginOfSafetyData;
  error: string;
  message: string;
  success: boolean;
}

// SEC Filings by Sector
export interface SecFilingItem {
  acceptedDate: string;
  changePercent: number;
  cik: string;
  filingDate: string;
  filingDatePrice: number;
  finalLink: string;
  formType: string;
  link: string;
  symbol: string;
}

export interface SecFilingsBySectorData {
  currentPrice: number;
  financials: SecFilingItem[];
  material_news_releases: SecFilingItem[];
  others: SecFilingItem[];
  ownership: SecFilingItem[];
  proxy: SecFilingItem[];
}

export interface GetSecFilingsBySectorQuery {
  ticker: string;
  year: number;
}

export interface GetSecFilingsBySectorResponse {
  data: SecFilingsBySectorData;
  error: string;
  message: string;
  success: boolean;
}

// SWOT Score
export interface SwotScoreData {
  action: string;
  altmanZScore: number;
  analystUpside: number;
  assetTurnoverTTM: number;
  beta: number;
  cashRatioTTM: number;
  consensus: string;
  currentGrossMargin: number;
  currentPrice: number;
  currentRatio: number;
  currentRatioTTM: number;
  currentRevenueGrowth: number;
  date: string;
  debtToAssetsRatioTTM: number;
  debtToCapitalRatioTTM: number;
  debtToEquityRatioTTM: number;
  dividendPayoutRatioTTM: number;
  dividendYieldTTM: number;
  ebitMarginTTM: number;
  ebitdaMarginTTM: number;
  enterpriseValueTTM: number;
  epsActual: number;
  epsEstimated: number;
  epsGrowth: number;
  freeCashFlow: number;
  freeCashFlowMargin: number;
  freeCashFlowToEquityTTM: number;
  freeCashFlowToFirmTTM: number;
  freeCashFlowYield: number;
  grossProfitMarginTTM: number;
  growthDividendPaid: number;
  interestCoverage: number;
  interestCoverageRatio: number;
  inventoryTurnoverTTM: number;
  marketCap: number;
  meaning: string;
  negativeFCFCount: number;
  netDebtToEBITDA: number;
  netProfitMarginTTM: number;
  operatingCashFlow: number;
  operatingProfitMarginTTM: number;
  operatingReturnOnAssetsTTM: number;
  opportunities: number;
  opportunitiesDescriptions: string[];
  opportunityAnalystUpside: boolean;
  opportunityRevenueGrowth: boolean;
  previousGrossMargin: number;
  previousRevenueGrowth: number;
  priceToBookRatioTTM: number;
  priceToEarningsGrowthRatioTTM: number;
  priceToEarningsRatioTTM: number;
  priceToFreeCashFlowRatioTTM: number;
  priceToSalesRatioTTM: number;
  quickRatioTTM: number;
  rating: string;
  returnOnAssetsTTM: number;
  returnOnCapitalEmployedTTM: number;
  returnOnEquityTTM: number;
  returnOnInvestedCapitalTTM: number;
  revenueGrowth: number;
  roe: number;
  roic: number;
  score: number;
  strengthFCFMargin: boolean;
  strengthLowLeverage: boolean;
  strengthROICROE: boolean;
  strengths: number;
  strengthsDescriptions: string[];
  symbol: string;
  targetConsensus: number;
  threatGrossMargin: boolean;
  threatNegativeNews: boolean;
  threats: number;
  threatsDescriptions: string[];
  totalPurchases: number;
  totalSales: number;
  weaknessAltmanZ: boolean;
  weaknessInterestCoverage: boolean;
  weaknessNegativeFCF: boolean;
  weaknesses: number;
  weaknessesDescriptions: string[];
  workingCapitalTTM: number;
}

export interface GetSwotScoreQuery {
  symbol: string;
  tabAccessToken?: string;
}

export interface GetSwotScoreResponse {
  data: SwotScoreData;
  error: string;
  message: string;
  success: boolean;
}

// Key Stats
export interface RangeData {
  high: number;
  low: number;
}

export interface KeyStatsData {
  averageVolume: number;
  consensusRating: string;
  dividendYield: number;
  fiftyDaysRange: RangeData;
  fiftyTwoWeekRange: RangeData;
  marketCapitalization: number;
  peRatio: number;
  priceTarget: number;
  todayRange: RangeData;
  volume: number;
}

export interface GetKeyStatsQuery {
  symbol: string;
  tabAccessToken?: string;
}

export interface GetKeyStatsResponse {
  data: KeyStatsData;
  error: string;
  message: string;
  success: boolean;
}

// Stock News
export interface StockNewsItem {
  image: string;
  publishedDate: string;
  publisher: string;
  site: string;
  symbol: string;
  text: string;
  title: string;
  url: string;
}

export interface GetStockNewsQuery {
  symbols: string;
  page?: number;
  limit?: number;
}

export interface GetStockNewsResponse {
  data: StockNewsItem[];
  error: string;
  message: string;
  success: boolean;
}

// Company Profile
export interface CompanyProfileData {
  address: string;
  city: string;
  country: string;
  description: string;
  employees: string;
  founded: string;
  image: string;
  industry: string;
  phone: string;
  sector: string;
  state: string;
  website: string;
  zip: string;
}

export interface GetCompanyProfileQuery {
  symbol: string;
  tabAccessToken?: string;
}

export interface GetCompanyProfileResponse {
  data: CompanyProfileData;
  error: string;
  message: string;
  success: boolean;
}

// Annualized Return types
export interface AnnualizedReturnData {
  symbol: string;
  "1D": number;
  "5D": number;
  "1M": number;
  "3M": number;
  "6M": number;
  ytd: number;
  "1Y": number;
  "3Y": number;
  "5Y": number;
  "10Y": number;
  max: number;
}

export interface BulkAnnualizedReturnRequest {
  symbols: string[];
}

export interface BulkAnnualizedReturnItem {
  symbol: string;
  annualizedReturn: AnnualizedReturnData;
}

export interface BulkAnnualizedReturnResponse {
  data: BulkAnnualizedReturnItem[];
  error: string;
  message: string;
  success: boolean;
}

// Quotes types
export interface QuoteItem {
  symbol: string;
  name: string;
  price: number;
  changePercentage: number;
  change: number;
  volume: number;
  dayLow: number;
  dayHigh: number;
  yearHigh: number;
  yearLow: number;
  marketCap: number;
  priceAvg50: number;
  priceAvg200: number;
  exchange: string;
  open: number;
  previousClose: number;
  timestamp: number;
}

export interface GetQuotesResponse {
  success: boolean;
  data: QuoteItem[];
}

// Historical Sector Performance types
export interface SectorPerformanceDataItem {
  averageChange: number;
  date: string;
  exchange: string;
  sector: string;
}

export interface SectorPerformanceSector {
  averageChange: number;
  data: SectorPerformanceDataItem[];
  from: string;
  sector: string;
  to: string;
}

export interface GetHistoricalSectorPerformanceQuery {
  sector?: string;
  from?: string;
  to?: string;
}

export interface GetHistoricalSectorPerformanceResponse {
  success: boolean;
  data: {
    sectors: SectorPerformanceSector[];
  };
  error: string;
  message: string;
}

// Historical Dividends types
export interface HistoricalDividendItem {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  adjClose: number;
  volume: number;
  unadjustedVolume: number;
  change: number;
  changePercent: number;
  vwap: number;
  label: string;
  changeOverTime: number;
}

export interface GetHistoricalDividendsQuery {
  symbol: string;
  limit?: number;
}

export interface GetHistoricalDividendsResponse {
  success: boolean;
  data: {
    symbol: string;
    historical: HistoricalDividendItem[];
  };
}

// Stock Analysis types
export interface StockAnalysisHistoricalPriceItem {
  adjClose: number;
  change: number;
  changeOverTime: number;
  changePercent: number;
  close: number;
  date: string;
  high: number;
  label: string;
  low: number;
  open: number;
  unadjustedVolume: number;
  volume: number;
  vwap: number;
}

export interface StockAnalysisHistoricalPrice {
  historical: StockAnalysisHistoricalPriceItem[];
  symbol: string;
}

export interface StockAnalysisPriceTargetConsensus {
  symbol: string;
  targetConsensus: number;
  targetHigh: number;
  targetLow: number;
  targetMedian: number;
}

export interface GetStockAnalysisQuery {
  symbol: string;
  tabAccessToken?: string;
}

export interface GetStockAnalysisResponse {
  data: {
    historicalPrice: StockAnalysisHistoricalPrice;
    priceTargetConsensus: StockAnalysisPriceTargetConsensus[];
  };
  error: string;
  message: string;
  success: boolean;
}

// Analyst Description types
export interface AnalystDescriptionData {
  allTimeAvgPriceTarget: number;
  consensus: string;
  currentPrice: number;
  description: string;
  directionWord: string;
  percentage: number;
  symbol: string;
  targetConsensus: number;
  targetHigh: number;
  targetLow: number;
  targetMedian: number;
  tickerName: string;
  totalAnalysts: number;
}

export interface GetAnalystDescriptionQuery {
  symbol: string;
  tabAccessToken?: string;
}

export interface GetAnalystDescriptionResponse {
  success: boolean;
  data: AnalystDescriptionData;
  error: string;
  message: string;
}

// Analyst rating description query parameters
export interface GetAnalystRatingDescriptionQuery {
  symbol: string;
  tabAccessToken?: string;
}

// Analyst rating description data
export interface AnalystRatingDescriptionData {
  avgRatingLabel: string;
  buyCount: number;
  consensusCount: number;
  consensusLabel: string;
  currentPrice: number;
  firstSentence: string;
  fullText: string;
  holdCount: number;
  secondSentence: string;
  sellCount: number;
  strongBuyCount: number;
  strongSellCount: number;
  symbol: string;
  targetPrice: number;
  targetPriceDiffPercent: number;
  tickerName: string;
  totalAnalysts: number;
  weightedAverageScore: number;
}

// Analyst rating description response
export interface GetAnalystRatingDescriptionResponse {
  data: AnalystRatingDescriptionData;
  error: string;
  message: string;
  success: boolean;
}

export interface SnaptradeAccount {
  access_level: "private" | "public" | string;
  /** SnapTrade permission on this account's connection: read or trade. */
  connection_type?: string;
  /**
   * Whether the brokerage itself supports trading through SnapTrade at all.
   * When false (e.g. Interactive Brokers), the connection can never trade, so
   * reconnecting to upgrade it is pointless — show a view-only note instead.
   */
  broker_allows_trading?: boolean;
  created_at: string;
  id: string;
  name: string;
  share_hash?: string;
  user_id: string;
}

export interface GetSnaptradeAccountsResponse {
  data: SnaptradeAccount[];
  error: string;
  message: string;
  success: boolean;
}

export interface SnaptradeHolding {
  /** Analyst consensus (buy / hold / sell); empty when unavailable. */
  analyst_rating?: string;
  /** Akinda ranking ("average score"); omitted when unavailable. */
  akinda_score?: number;
  change: number;
  company: string;
  current_price: number;
  halal_status?: string;
  id: string;
  /** Optional backend-provided company logo. Front-end falls back to a
   *  ticker-based URL when this is missing so broker rows look identical
   *  to manual portfolio rows. */
  logoURL?: string;
  price: number;
  quantity: number;
  sector: string;
  ticker: string;
  user_id: string;
  value: number;
}

export interface GetSnaptradeAccountHoldingsResponse {
  data: SnaptradeHolding[];
  error: string;
  message: string;
  success: boolean;
}

export interface RefreshSnaptradeAccountResponse {
  data: Record<string, unknown>;
  error: string;
  message: string;
  success: boolean;
}

export interface SnaptradeLoginResponse {
  data: string;
  error: string;
  message: string;
  success: boolean;
}

export type LiveOrderSide = "BUY" | "SELL";
export type LiveOrderType = "Market" | "Limit" | "Stop" | "StopLimit";
export type LiveTimeInForce = "Day" | "GTC";

export interface TradableSymbol {
  ticker: string;
  name: string;
  universal_symbol_id: string;
  exchange: string;
  currency: string;
}

export interface SearchTradableSymbolsResponse {
  success: boolean;
  data: TradableSymbol[];
  message?: string;
  error?: string;
}

export interface TradingBalances {
  buying_power: number;
  cash: number;
  currency: string;
}

export interface GetTradingBalancesResponse {
  success: boolean;
  data: TradingBalances;
  message?: string;
  error?: string;
}

export interface TradeOrder {
  id: string;
  user_id?: string;
  provider?: string;
  account_id: string;
  broker_order_id?: string;
  ticker: string;
  side: LiveOrderSide | string;
  order_type: LiveOrderType | string;
  time_in_force: LiveTimeInForce | string;
  quantity: number;
  limit_price?: number;
  stop_price?: number;
  status: string;
  broker_status?: string;
  filled_quantity?: number;
  avg_fill_price?: number;
  reject_reason?: string;
  created_at: string;
  updated_at?: string;
}

export interface PreviewTradeOrderResponse {
  success: boolean;
  data: {
    preview_id?: string;
    estimated_price?: number;
    estimated_amount?: number;
    estimated_commission?: number;
    remaining_cash?: number;
    currency?: string;
    preview_supported?: boolean;
  };
  message?: string;
  error?: string;
}

export interface PlaceTradeOrderBody {
  ticker: string;
  side: LiveOrderSide;
  order_type: LiveOrderType;
  time_in_force: LiveTimeInForce;
  quantity: number;
  limit_price?: number;
  stop_price?: number;
  idempotency_key: string;
  preview_id?: string;
}

export interface PlaceTradeOrderResponse {
  success: boolean;
  data: TradeOrder;
  message?: string;
  error?: string;
}

export interface ListTradeOrdersQuery {
  page?: number;
  limit?: number;
  status?: string;
}

export interface ListTradeOrdersResponse {
  success: boolean;
  data: TradeOrder[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  message?: string;
  error?: string;
}

export const LIVE_OPEN_ORDER_STATUSES = new Set([
  "PENDING_SUBMIT",
  "SUBMITTED",
  "PENDING",
  "PARTIALLY_FILLED",
]);
